/**
 * DV-INF-001: Shared Admin Guard Component
 * Standardized admin access gating for Demo Viewer components
 */

import React from 'react';
import { useServerAuth } from '@/hooks/useAuth-cookie';
import { PermissionNotice } from './ErrorNotices';

export interface AdminGuardProps {
  children: React.ReactNode;
  fallback?: React.ReactNode;
  showLoading?: boolean;
  className?: string;
}

/**
 * Guard component that only renders children for authenticated admin users
 *
 * @param children - Content to render for admin users
 * @param fallback - Custom fallback component (defaults to PermissionNotice)
 * @param showLoading - Whether to show loading state during auth check
 * @param className - Additional CSS classes
 */
export function AdminGuard({
  children,
  fallback,
  showLoading = true,
  className = ''
}: AdminGuardProps) {
  const { isAdmin, loading, isAuthenticated } = useServerAuth();

  // Show loading state if enabled and auth is still checking
  if (loading && showLoading) {
    return (
      <div
        className={`flex items-center justify-center py-8 ${className}`}
        style={{ color: 'var(--fg-muted)' }}
        data-testid="admin-guard-loading"
      >
        <div className="flex items-center gap-2 text-sm">
          <div className="w-4 h-4 border-2 border-current border-t-transparent rounded-full animate-spin" />
          Checking admin permissions...
        </div>
      </div>
    );
  }

  // User is not authenticated or not an admin
  if (!isAuthenticated || !isAdmin) {
    // Use custom fallback or default permission notice
    if (fallback) {
      return <>{fallback}</>;
    }

    const permissionError = {
      type: 'permission_denied' as const,
      status: 403,
      message: 'Admin access required',
      userMessage: 'Admin access required',
      remediation: [
        'Please ensure you\'re signed in with an admin account',
        'Verify your account has admin permissions',
        'Try refreshing the page to update your session',
        'Contact support if you believe this is an error'
      ],
      endpoint: '/admin',
      retryable: false
    };

    return (
      <PermissionNotice
        error={permissionError}
      />
    );
  }

  // User is authenticated and admin - render children
  return <div className={className}>{children}</div>;
}

/**
 * Hook for admin access checking with standardized return values
 * Provides consistent admin state across Demo Viewer components
 * This is a thin wrapper around useServerAuth for consistency
 */
export function useAdminAccess() {
  const { isAdmin, loading, isAuthenticated, user } = useServerAuth();

  return {
    isAdmin: !!isAdmin,
    adminLoading: loading,
    isAuthenticated: !!isAuthenticated,
    hasAdminRole: user?.role === 'admin' || user?.isAdmin || false,
    user,
    // Helper computed properties
    canAccessAdminFeatures: isAuthenticated && isAdmin,
    shouldShowAdminContent: !loading && isAuthenticated && isAdmin,
  };
}

/**
 * Higher-order component for admin-only pages
 */
export function withAdminGuard<P extends object>(
  Component: React.ComponentType<P>,
  options?: Omit<AdminGuardProps, 'children'>
) {
  const AdminGuardedComponent = (props: P) => (
    <AdminGuard {...options}>
      <Component {...props} />
    </AdminGuard>
  );

  AdminGuardedComponent.displayName = `withAdminGuard(${Component.displayName || Component.name})`;

  return AdminGuardedComponent;
}